/**
 * Softening a layer.
 *
 * Every Roblox shading tutorial says the same thing at every step: paint the
 * shadow, then blur it "just a little". A hard-edged shadow reads as a shape
 * drawn on a garment; a soft one reads as light. It is the difference between
 * the flat look and the one people are copying.
 *
 * ## Destructive, deliberately
 *
 * This rewrites the layer's pixels rather than becoming a property of it. A
 * live blur would re-filter the whole layer on every recomposite — during a
 * stroke that is once a frame, for a 585×559 surface, for every blurred layer
 * in the stack. Baking costs one `filter` pass and makes the composite free
 * afterwards, and it is also what every raster editor does: filters are
 * destructive unless you opt into smart objects.
 *
 * The consequence is that blurring twice compounds, and the dialog says so.
 * Undo is the way back, which is why the caller writes a pixel edit.
 */

import type { Rect } from "../../shared/format";

/** Below this nothing visibly changes; above it a 585px canvas turns to soup. */
export const BLUR_MIN = 0.5;
export const BLUR_MAX = 8;
/** Half a pixel. Finer than this is not perceptible on a template this size. */
export const BLUR_STEP = 0.5;
/** Where the slider starts: enough to see, small enough to keep a shadow's shape. */
export const BLUR_DEFAULT = 2;

/**
 * A radius the filter will accept.
 *
 * `ctx.filter` takes a CSS length, and a NaN or negative one is not an error —
 * the whole filter string is invalid and is silently ignored, so the layer is
 * redrawn unblurred and the only symptom is that the slider does nothing.
 */
export function clampRadius(radius: number): number {
  if (!Number.isFinite(radius)) return BLUR_MIN;
  const stepped = Math.round(radius / BLUR_STEP) * BLUR_STEP;
  return Math.min(BLUR_MAX, Math.max(BLUR_MIN, stepped));
}

/**
 * Whether this browser can blur at all.
 *
 * `ctx.filter` is the only blur a 2D canvas has, and where it is unsupported
 * the assignment is not an error — it silently stays "none", the layer redraws
 * untouched, and without this check the app would report a successful blur and
 * write an undo step for a picture that never changed. Safari only shipped it
 * in 17, which is squarely inside the iPad audience this editor is built for.
 */
export function canBlur(doc: Document = document): boolean {
  const probe = doc.createElement("canvas").getContext("2d");
  if (!probe) return false;
  probe.filter = "blur(2px)";
  return probe.filter === "blur(2px)";
}

/*
  Scratch canvases, reused for the life of the page.

  `blurInto` runs on every frame of a slider drag, and it used to build two
  585x559 canvases per call. Two allocations a frame is enough garbage to show
  up as stutter on the drag that is meant to be showing a smooth preview.

  `origin` holds the unblurred pixels. It is keyed by the ImageData it was
  filled from, so a whole drag — which blurs the same original over and over —
  puts those pixels up once instead of on every frame.
*/
let originCanvas: HTMLCanvasElement | null = null;
let originFrom: ImageData | null = null;
let maskCanvas: HTMLCanvasElement | null = null;
/** The document the cached scratches belong to, so a test never reuses the page's. */
let scratchDoc: Document | null = null;

function scratch(
  existing: HTMLCanvasElement | null,
  w: number,
  h: number,
  doc: Document,
): HTMLCanvasElement {
  const canvas = existing ?? doc.createElement("canvas");
  if (canvas.width !== w || canvas.height !== h) {
    canvas.width = w;
    canvas.height = h;
  }
  return canvas;
}

/**
 * Blur `from` into `canvas`, replacing what is there.
 *
 * Takes the source pixels as an ImageData rather than reading the canvas,
 * because the caller holds the ORIGINAL: dragging the slider must blur the
 * unblurred layer each time, not blur what the last drag position left behind.
 * Re-reading the canvas would compound the preview into mud within a second.
 *
 * Drawn through a scratch rather than filtering in place — a canvas cannot draw
 * itself through its own filter, and `putImageData` ignores `filter` entirely
 * (it is a pixel write, not a draw).
 *
 * ## Zones
 *
 * `zones` are areas ink may not blur out of. Pass the template's face rects for
 * an artwork layer and nothing for a placement, whose pixels are its own image
 * and have no faces in them.
 *
 * This is not a detail. A gaussian reaches far past the 2px gutter between two
 * faces: measured, ink stopping at the last texel of `torso.right` lands at
 * alpha 90 on the first pixel of `torso.front` at radius 8, and is still
 * visible twelve pixels in. Those are different surfaces on the avatar, so an
 * unzoned blur paints a ghost of the side of the torso onto its front — the
 * same bleed the brush already guards against by masking a stroke to the
 * regions it started in.
 *
 * Each zone is masked, blurred, and written back within itself, so ink can
 * neither leave a face nor arrive from its neighbour.
 */
export interface BlurIntoOptions {
  /** Areas ink may not blur out of. Omit for a placement, which has no faces. */
  zones?: readonly Rect[];
  /** Injectable so the zone masking can be tested without a browser. */
  doc?: Document;
}

/** @see BlurIntoOptions */
export function blurInto(
  canvas: HTMLCanvasElement,
  from: ImageData,
  radius: number,
  opts: BlurIntoOptions = {},
): boolean {
  const { zones, doc = document } = opts;
  const ctx = canvas.getContext("2d");
  if (!ctx) return false;

  const { width: w, height: h } = canvas;
  if (scratchDoc !== doc) {
    originCanvas = null;
    maskCanvas = null;
    originFrom = null;
    scratchDoc = doc;
  }
  originCanvas = scratch(originCanvas, w, h, doc);
  const octx = originCanvas.getContext("2d");
  if (!octx) return false;
  if (originFrom !== from) {
    octx.putImageData(from, 0, 0);
    originFrom = from;
  }

  const filter = `blur(${clampRadius(radius)}px)`;

  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.globalCompositeOperation = "source-over";
  ctx.globalAlpha = 1;
  ctx.clearRect(0, 0, w, h);

  if (!zones) {
    ctx.filter = filter;
    ctx.drawImage(originCanvas, 0, 0);
    ctx.restore();
    return true;
  }

  maskCanvas = scratch(maskCanvas, w, h, doc);
  const mctx = maskCanvas.getContext("2d");
  if (!mctx) {
    ctx.restore();
    return false;
  }

  /*
    Every zone, plus everything that is in none of them.

    The leftovers matter: gutters and the dead space around the net are more
    than half the sheet, they never render on the avatar, and blurring them
    into oblivion would be silently deleting pixels the editor still shows.
    They blur among themselves instead, which keeps them intact and still
    cannot push ink onto a face.
  */
  for (const zone of zones) {
    mctx.save();
    mctx.setTransform(1, 0, 0, 1, 0, 0);
    mctx.globalCompositeOperation = "source-over";
    /*
      The WHOLE mask, not just this zone.

      Clearing only the zone leaves the previous zone's ink sitting on the
      scratch, and the gaussian samples past the edge of the clip — so the
      neighbouring face's pixels get blurred in and pass through this zone's
      clip. That is the same cross-face bleed this function exists to stop,
      reintroduced by sharing the canvas. Measured: ink confined to
      `torso.right` still arrived at alpha 96 inside `torso.front`.

      Deliberately not a rect expanded by some multiple of the radius. That
      would be faster and would make correctness depend on a guess about how
      far a browser's gaussian reaches, which is exactly the assumption that
      fails silently.
    */
    mctx.clearRect(0, 0, w, h);
    mctx.beginPath();
    mctx.rect(zone.x, zone.y, zone.w, zone.h);
    mctx.clip();
    mctx.drawImage(originCanvas, 0, 0);
    mctx.restore();

    ctx.save();
    ctx.beginPath();
    ctx.rect(zone.x, zone.y, zone.w, zone.h);
    ctx.clip();
    ctx.filter = filter;
    ctx.drawImage(maskCanvas, 0, 0);
    ctx.restore();
  }

  // The complement, as one zone: fill the sheet, punch out every zone, and let
  // evenodd decide what is left.
  const leftovers = new Path2D();
  leftovers.rect(0, 0, w, h);
  for (const zone of zones) leftovers.rect(zone.x, zone.y, zone.w, zone.h);

  mctx.save();
  mctx.setTransform(1, 0, 0, 1, 0, 0);
  mctx.globalCompositeOperation = "source-over";
  mctx.clearRect(0, 0, w, h);
  mctx.clip(leftovers, "evenodd");
  mctx.drawImage(originCanvas, 0, 0);
  mctx.restore();

  ctx.save();
  ctx.clip(leftovers, "evenodd");
  ctx.filter = filter;
  ctx.drawImage(maskCanvas, 0, 0);
  ctx.restore();

  ctx.restore();
  return true;
}

/** Put the original pixels back, for a cancelled preview. */
export function restorePixels(canvas: HTMLCanvasElement, from: ImageData): void {
  canvas.getContext("2d")?.putImageData(from, 0, 0);
}

export interface BlurDialogOptions {
  /** Layer name, for the heading — so it is obvious what is about to change. */
  name: string;
  /**
   * True when the radius is in the placement's own pixels rather than the
   * template's, which changes what a number means and is worth saying.
   */
  isPlacement: boolean;
  /** Redraw at this radius. Called on every slider move. */
  onPreview: (radius: number) => void;
  /** Keep it. */
  onApply: (radius: number) => void;
  /** Put it back. Called on Cancel and on Escape, exactly once. */
  onCancel: () => void;
  doc?: Document;
}

const STYLE_ID = "blur-dialog-style";

const CSS = `
.blur-dlg {
  border: var(--border-width) solid var(--panel-border);
  border-radius: var(--radius-md);
  background: var(--panel-bg);
  color: var(--text);
  padding: var(--space-md);
  inline-size: min(320px, calc(100vw - 32px));
}
.blur-dlg::backdrop { background: var(--scrim); }
.blur-dlg h3 { margin: 0 0 var(--space-2xs); font-size: var(--type-size-md); }
.blur-dlg p { margin: 0 0 var(--space-sm); font-size: var(--type-size-xs); color: var(--text-muted); }
.blur-dlg__row { display: grid; grid-template-columns: 1fr 44px; align-items: center; gap: var(--space-xs); }
.blur-dlg__row input { inline-size: 100%; }
.blur-dlg__value { text-align: end; font-variant-numeric: tabular-nums; font-size: var(--type-size-xs); }
.blur-dlg__actions { display: flex; justify-content: flex-end; gap: var(--space-xs); margin-block-start: var(--space-md); }
`;

/**
 * Ask for a radius, previewing as it moves.
 *
 * The preview is the real layer, not a thumbnail: a blur is judged against the
 * artwork around it, and a 60px swatch cannot show whether a shadow still has
 * its shape. Cancelling puts the pixels back.
 */
export function openBlurDialog(opts: BlurDialogOptions): void {
  const doc = opts.doc ?? document;

  if (!doc.getElementById(STYLE_ID)) {
    const style = doc.createElement("style");
    style.id = STYLE_ID;
    style.textContent = CSS;
    doc.head.appendChild(style);
  }

  const dialog = doc.createElement("dialog");
  dialog.className = "blur-dlg";

  const heading = doc.createElement("h3");
  heading.textContent = `Blur ${opts.name}`;

  const note = doc.createElement("p");
  /*
    Two things worth saying before somebody presses Apply, and neither is
    obvious from a slider: this rewrites the pixels, and on a placement the
    radius is in the image's own pixels — so a badge scaled to 40% shows a 40%
    blur and the number does not mean template pixels.
  */
  note.textContent = opts.isPlacement
    ? "Rewrites the image's pixels, measured in the image's own pixels rather than the template's. Blurring again blurs the result. Undo takes it back."
    : "Rewrites the layer's pixels. Blurring again blurs the result. Undo takes it back.";

  const row = doc.createElement("div");
  row.className = "blur-dlg__row";
  const input = doc.createElement("input");
  input.type = "range";
  input.min = String(BLUR_MIN);
  input.max = String(BLUR_MAX);
  input.step = String(BLUR_STEP);
  input.value = String(BLUR_DEFAULT);
  input.setAttribute("aria-label", "Blur radius in pixels");
  const value = doc.createElement("span");
  value.className = "blur-dlg__value";
  value.textContent = `${BLUR_DEFAULT} px`;
  row.appendChild(input);
  row.appendChild(value);

  const actions = doc.createElement("div");
  actions.className = "blur-dlg__actions";
  const cancel = doc.createElement("button");
  cancel.type = "button";
  cancel.className = "btn";
  cancel.textContent = "Cancel";
  const apply = doc.createElement("button");
  apply.type = "button";
  apply.className = "btn btn-primary";
  apply.textContent = "Blur";
  actions.appendChild(cancel);
  actions.appendChild(apply);

  for (const child of [heading, note, row, actions]) dialog.appendChild(child);
  doc.body.appendChild(dialog);

  /*
    Whether the pixels have been kept, so the teardown knows what to do.
    Defaulting to "put it back" means the only way to keep a blur is to have
    asked for it.
  */
  let kept = false;
  /** Teardown runs exactly once, whichever route got here first. */
  let settled = false;

  /**
   * Leave the layer in a definite state and drop the dialog.
   *
   * Called directly by the buttons AND from the `close` event, because the two
   * ways out must not depend on each other. Hanging the restore solely off
   * `close` means one environment that does not deliver that event — and there
   * is at least one, which is how this was found — leaves the layer stuck in
   * preview with no way back except undo. Idempotent, so both routes firing is
   * harmless.
   */
  const settle = () => {
    if (settled) return;
    settled = true;
    if (!kept) opts.onCancel();
    dialog.remove();
  };

  input.addEventListener("input", () => {
    const radius = clampRadius(Number(input.value));
    value.textContent = `${radius} px`;
    opts.onPreview(radius);
  });

  cancel.addEventListener("click", () => {
    dialog.close();
    settle();
  });
  apply.addEventListener("click", () => {
    kept = true;
    opts.onApply(clampRadius(Number(input.value)));
    dialog.close();
    settle();
  });
  // Escape, which closes a modal dialog natively without touching a button.
  dialog.addEventListener("close", settle);

  dialog.showModal();
  // Preview immediately, so opening the dialog already shows what it will do.
  opts.onPreview(BLUR_DEFAULT);
}
